import type { SocialMedia } from '../../lib/workflowsApi';

/**
 * Instagram path prefixes that address content rather than an account. The
 * scraper takes a username, so these URLs have nothing to enrich.
 *
 * Kept in sync with `INSTAGRAM_NON_PROFILE_SECTIONS` in
 * `backend-nest/src/lead-enrichment/utils/profile-url-platform.ts`.
 */
const INSTAGRAM_NON_PROFILE_SECTIONS = new Set([
  'p',
  'reel',
  'reels',
  'stories',
  'explore',
  'tv',
  'accounts',
  'directory',
]);

const PLATFORM_HOST: Partial<Record<SocialMedia, string>> = {
  LinkedIn: 'linkedin.com',
  Instagram: 'instagram.com',
};

function parseUrl(url: string): URL | null {
  try {
    const parsed = new URL(url.trim());
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed : null;
  } catch {
    return null;
  }
}

function hostMatches(hostname: string, domain: string): boolean {
  const host = hostname.toLowerCase().replace(/^www\./, '');
  return host === domain || host.endsWith(`.${domain}`);
}

/**
 * True only when the URL addresses an enrichable ACCOUNT on the selected
 * platform: `linkedin.com/in/<publicId>` or `instagram.com/<username>`.
 *
 * Validating the host alone is not enough — the backend derives the Instagram
 * username from the first path segment, so a content URL like
 * `instagram.com/p/<shortcode>` would resolve to the username "p" and scrape an
 * unrelated account. Platforms with no enrichment pipeline never validate.
 */
export function isValidProfileUrl(url: string, platform: SocialMedia): boolean {
  const domain = PLATFORM_HOST[platform];
  if (!domain) return false;

  const parsed = parseUrl(url);
  if (!parsed || !hostMatches(parsed.hostname, domain)) return false;

  const segments = parsed.pathname.split('/').filter(Boolean);

  if (platform === 'LinkedIn') {
    const [section, publicId] = segments;
    return section?.toLowerCase() === 'in' && Boolean(publicId);
  }

  const [username] = segments;
  return Boolean(username) && !INSTAGRAM_NON_PROFILE_SECTIONS.has(username.toLowerCase());
}
